"use client";

import { postData } from "@/request/api";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle2,
  Clock,
  FileText,
  HeadphonesIcon,
  Phone,
  Send,
  ShieldCheck,
  User,
} from "lucide-react";
import { useLocale } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";

type SupportFormData = {
  customerName: string;
  phone: string;
  invoiceNumber: string;
};

export default function Support() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SupportFormData>({
    defaultValues: { customerName: "", phone: "", invoiceNumber: "" },
  });

  const phoneValue = watch("phone");
  const invoiceValue = watch("invoiceNumber");

  const onSubmit = async (data: SupportFormData) => {
    setServerError(null);
    setIsLoading(true);
    try {
      await postData("support", data);
      setIsSubmitted(true);
    } catch (err: any) {
      setServerError(
        err?.response?.data?.message ||
          (isAr
            ? "حدث خطأ، يرجى المحاولة مرة أخرى"
            : "An error occurred, please try again"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: ShieldCheck,
      title: isAr ? "ضمان موثوق" : "Reliable Warranty",
      desc: isAr
        ? "نضمن لك جودة منتجاتنا وخدماتنا"
        : "We guarantee the quality of our products",
    },
    {
      icon: Clock,
      title: isAr ? "استجابة سريعة" : "Quick Response",
      desc: isAr
        ? "يتواصل فريقنا معك في أسرع وقت"
        : "Our team responds as soon as possible",
    },
    {
      icon: HeadphonesIcon,
      title: isAr ? "دعم متخصص" : "Expert Support",
      desc: isAr
        ? "فريق متخصص لحل جميع استفساراتك"
        : "Specialized team for all your inquiries",
    },
  ];

  return (
    <section
      className="relative bg-brand-creamy min-h-screen overflow-hidden"
      dir={isAr ? "rtl" : "ltr"}
    >
      {/* Ambient blobs */}
      <div className="absolute -top-60 -right-60 w-[700px] h-[700px] bg-brand-blue/8 rounded-full blur-[200px] pointer-events-none" />
      <div className="absolute -bottom-60 -left-60 w-[700px] h-[700px] bg-brand-red/8 rounded-full blur-[200px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-brand-navy/4 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative container mx-auto px-6 pt-20 pb-32">
        {/* ─── Hero heading ─── */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 mb-5 px-5 py-2 rounded-full bg-brand-red/10 text-brand-red text-xs font-black tracking-[0.35em] uppercase">
            <HeadphonesIcon className="w-4 h-4" />
            {isAr ? "خدمة العملاء" : "Customer Service"}
          </span>

          <h1 className="text-4xl md:text-7xl font-black text-brand-navy leading-[1.05] tracking-tight">
            {isAr ? "نحن هنا لمساعدتك" : "We're Here to Help"}
          </h1>

          <p className="mt-6 text-base md:text-lg text-brand-navy/60 font-medium leading-relaxed max-w-xl mx-auto">
            {isAr
              ? "للحصول على شهادة الضمان والدعم الفني، أدخل بياناتك وسيتواصل فريقنا معك في أسرع وقت."
              : "To obtain your warranty certificate and technical support, enter your details and our team will contact you shortly."}
          </p>
        </motion.div>

        {/* ─── Feature cards ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16 max-w-4xl mx-auto">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.1 }}
              className="flex items-start gap-4 p-6 bg-white rounded-4xl border border-brand-navy/6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-navy/8 flex items-center justify-center shrink-0">
                <f.icon className="w-5 h-5 text-brand-navy" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-brand-navy">{f.title}</h4>
                <p className="text-xs text-brand-navy/50 mt-1 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ─── Main form card ─── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-white rounded-[3rem] border border-brand-navy/6 shadow-2xl overflow-hidden">
            {/* Card header */}
            <div className="bg-brand-navy px-10 py-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-white/5 rounded-full" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-brand-red/20 rounded-full" />
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                  {isAr ? "أدخل بياناتك" : "Enter Your Details"}
                </h2>
                <p className="text-white/50 text-sm mt-2">
                  {isAr
                    ? "جميع الحقول مطلوبة ما عدا واحداً من رقم الموبايل أو رقم الفاتورة"
                    : "All fields required except one of mobile or invoice number"}
                </p>
              </div>
            </div>

            {/* Card body */}
            <div className="px-10 py-10">
              <AnimatePresence mode="wait">
                {isSubmitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col items-center justify-center py-10 text-center"
                  >
                    <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mb-6">
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                    </div>
                    <h3 className="text-2xl font-black text-brand-navy mb-3">
                      {isAr ? "تم الإرسال بنجاح!" : "Request Sent!"}
                    </h3>
                    <p className="text-brand-navy/60 max-w-sm leading-relaxed">
                      {isAr
                        ? "شكراً لتواصلك معنا. سيتواصل فريق خدمة العملاء معك في أقرب وقت ممكن."
                        : "Thank you for reaching out. Our customer service team will contact you as soon as possible."}
                    </p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-7"
                  >
                    {/* Customer Name */}
                    <div>
                      <label className="flex items-center gap-2 text-xs font-black tracking-widest uppercase text-brand-navy/50 mb-3">
                        <User className="w-3.5 h-3.5" />
                        {isAr ? "اسم العميل" : "Customer Name"}
                        <span className="text-brand-red">*</span>
                      </label>
                      <input
                        {...register("customerName", {
                          required: isAr
                            ? "اسم العميل مطلوب"
                            : "Customer name is required",
                          minLength: {
                            value: 2,
                            message: isAr
                              ? "الاسم قصير جداً"
                              : "Name is too short",
                          },
                        })}
                        type="text"
                        placeholder={
                          isAr ? "أدخل اسمك الكامل" : "Enter your full name"
                        }
                        className="w-full px-5 py-4 bg-brand-creamy/60 border border-brand-navy/10 rounded-2xl text-brand-navy placeholder:text-brand-navy/30 focus:outline-none focus:border-brand-navy/40 focus:bg-white transition-all text-base font-medium"
                      />
                      {errors.customerName && (
                        <p className="mt-2 text-xs text-brand-red font-semibold">
                          {errors.customerName.message}
                        </p>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-px bg-brand-navy/8" />
                      <span className="text-xs text-brand-navy/40 font-bold tracking-wider">
                        {isAr ? "أدخل واحداً على الأقل" : "Enter at least one"}
                      </span>
                      <div className="flex-1 h-px bg-brand-navy/8" />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="flex items-center gap-2 text-xs font-black tracking-widest uppercase text-brand-navy/50 mb-3">
                        <Phone className="w-3.5 h-3.5" />
                        {isAr ? "رقم الموبايل" : "Mobile Number"}
                      </label>
                      <input
                        {...register("phone", {
                          validate: (val) => {
                            if (!val && !invoiceValue) {
                              return isAr
                                ? "يجب إدخال رقم الموبايل أو رقم الفاتورة"
                                : "Phone or invoice number is required";
                            }
                            return true;
                          },
                        })}
                        type="tel"
                        placeholder={
                          isAr ? "مثال: 01xxxxxxxxx" : "e.g. 01xxxxxxxxx"
                        }
                        dir="ltr"
                        className="w-full px-5 py-4 bg-brand-creamy/60 border border-brand-navy/10 rounded-2xl text-brand-navy placeholder:text-brand-navy/30 focus:outline-none focus:border-brand-navy/40 focus:bg-white transition-all text-base font-medium"
                      />
                    </div>

                    {/* OR separator */}
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-px bg-brand-navy/8" />
                      <div className="w-8 h-8 rounded-full bg-brand-navy/6 flex items-center justify-center">
                        <span className="text-xs font-black text-brand-navy/40">
                          {isAr ? "أو" : "OR"}
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-brand-navy/8" />
                    </div>

                    {/* Invoice Number */}
                    <div>
                      <label className="flex items-center gap-2 text-xs font-black tracking-widest uppercase text-brand-navy/50 mb-3">
                        <FileText className="w-3.5 h-3.5" />
                        {isAr ? "رقم الفاتورة" : "Invoice Number"}
                      </label>
                      <input
                        {...register("invoiceNumber", {
                          validate: (val) => {
                            if (!val && !phoneValue) {
                              return isAr
                                ? "يجب إدخال رقم الموبايل أو رقم الفاتورة"
                                : "Phone or invoice number is required";
                            }
                            return true;
                          },
                        })}
                        type="text"
                        placeholder={
                          isAr ? "أدخل رقم الفاتورة" : "Enter invoice number"
                        }
                        className="w-full px-5 py-4 bg-brand-creamy/60 border border-brand-navy/10 rounded-2xl text-brand-navy placeholder:text-brand-navy/30 focus:outline-none focus:border-brand-navy/40 focus:bg-white transition-all text-base font-medium"
                      />
                    </div>

                    {/* Validation error for phone/invoice */}
                    {(errors.phone || errors.invoiceNumber) && (
                      <p className="text-xs text-brand-red font-semibold -mt-4">
                        {errors.phone?.message || errors.invoiceNumber?.message}
                      </p>
                    )}

                    {/* Server error */}
                    {serverError && (
                      <div className="p-4 rounded-2xl bg-red-50 border border-red-200">
                        <p className="text-sm text-red-600 font-semibold text-center">
                          {serverError}
                        </p>
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex items-center justify-center gap-3 bg-brand-navy hover:bg-brand-navy/90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-black text-base px-8 py-4 rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-brand-navy/20 hover:-translate-y-0.5 active:translate-y-0"
                    >
                      {isLoading ? (
                        <>
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          {isAr ? "جارٍ الإرسال..." : "Sending..."}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          {isAr ? "إرسال الطلب" : "Send Request"}
                        </>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
